<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref, unref } from 'vue';
import { store } from '@/store';
import * as PleasedApi from '@/common/api/api.pleased';
import type { VisitorInfoFilled } from '@/common/types';
import { MAX_GUEST_FIELDS_CHAR, MIN_DESCRIPTION_CHAR_NUM } from '@/common/config';
import type { UserInformation } from '@/common/api/models.pleased';
import PlsButton1 from '@/components/PlsButton1.vue';
import GuestFormFields from '@/components/OpenRequestModal/GuestFormFields.vue';
import type {
  GuestFieldErrors,
  GuestFormBoundModel,
  GuestTicketForm,
} from '@/components/OpenRequestModal/openRequestForm.types';
import { useGuestTicketFormValidation } from '@/composables/guest-form';
import LoadingIcon from '@/components/icons/LoadingIcon.vue';
import { useI18n } from '@/plugins/i18n';
import { analytics } from '@/common/logs';
import { GENERAL_EVENTS } from '@/common/logs/events';
import { reportingService } from '@/common/reporting';
import { resolveGuestDisplayName } from '@/common/guestForm';
import { getFallbackUserName } from '@/common/userDisplayName';
import { FALLBACK_OPEN_REQUEST_DESCRIPTION } from '@/common/openRequest';
import * as BridgeEvents from '@/common/bridge/modern-bridge/bridge-events';
import {
  useGuestFormAllCustomisation,
  useGuestFormBackButton,
  useGuestFieldRequirements,
} from '../../composables/guest-form/useGuestFormCustomisation';
import { useOTPVerification } from '../../composables/guest-form/useOTPCustomisation';
import { useGuestFormOpenRequest } from '../../composables/guest-form/useGuestFormOpenRequest';
import { useGuestFormPrimaryActionButton } from '../../composables/guest-form/useGuestFormActionsCustomisation';
import {
  combineStyles,
  createBackgroundStyle,
  createBorderStyle,
  createTextStyle,
  resolveButtonStateColor,
} from '@/common/customisation/utils/styleUtils';
import GuestFormOtpVerification from './OtpVerification.vue';
import GuestFormTicketConfirmation from './TicketConfirmation.vue';
import { isSkipToLiveAgentFlow } from '@/common/skipToLiveAgent.utils';
import { useConnectConfig } from '@/stores/useConnectConfig';
import { useConfig } from '@/stores/useConfig';
import { useWidgetConversation } from '@/stores/useWidgetConversation';
import { useBotUser } from '@/stores/useBotUser';
import { storeToRefs } from 'pinia';
import { captureEventException, ErrorTrackingEvent } from '@/common/error-tracking';

interface Props {
  isOpenRequestFlow?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'submit:detail', payload: VisitorInfoFilled): void;
  (e: 'support-request:cancel', payload: Record<string, unknown>): void;
  (e: 'close-chatbot'): void;
  (e: 'back-to-login'): void;
}>();

const { t } = useI18n();

const { validateGuestTicketForm, fieldErrorMessage } = useGuestTicketFormValidation();

const { connectConfig } = storeToRefs(useConnectConfig());
const { config: widgetConfig } = storeToRefs(useConfig());

const effectiveLoginMethod = computed<'external' | 'hybrid' | 'none'>(() => {
  return widgetConfig.value?.loginMethod ?? 'hybrid';
});

const connectRequestSubject = computed(() => {
  const cfg = connectConfig.value;
  return (cfg?.category ?? '') + (cfg?.section?.trim()?.length ? `+ ${cfg.section}` : '') || 'Support Request:';
});

const widgetConversation = useWidgetConversation();
const botUserStore = useBotUser();

const guestCustomisation = useGuestFormAllCustomisation();
const { title, apiErrorBanner } = guestCustomisation;

const primaryAction = useGuestFormPrimaryActionButton();
const backButton = useGuestFormBackButton();
const requirements = useGuestFieldRequirements();
const { otpEnabled } = useOTPVerification();
const { createGuestOpenRequest } = useGuestFormOpenRequest();

/** Email is required (regardless of the customisation toggle) when the open-request flow needs OTP. */
const emailRequiredByOtp = computed(() => Boolean(props.isOpenRequestFlow && otpEnabled.value));

const loading = ref(false);
const currentStep = ref<'form' | 'otp' | 'confirmation'>('form');
const createdTicketId = ref('');
const requestErrorMessage = ref('');
const originalEmail = ref('');

const form = reactive<GuestTicketForm>({
  name: '',
  phone: '',
  email: '',
  description: '',
});

const error = reactive({
  name: '',
  email: '',
  description: '',
  phone: '',
});

const showDescriptionBox = computed(() => Boolean(props.isOpenRequestFlow));

const descriptionHint = computed(() => connectConfig.value?.descriptionMessage ?? t('global.describe'));

const guestData = computed(() => ({
  name: form.name,
  email: form.email,
  phone: form.phone,
  description: form.description,
}));

const isDescriptionExceed = computed(() => form.description.length > MAX_GUEST_FIELDS_CHAR.description);

const titleDisplay = computed(() => {
  const custom = title.titleText.value?.trim();
  if (custom) {
    return custom;
  }
  return props.isOpenRequestFlow ? t('page.guest.verify_details') : t('page.guest.enter_details');
});

const titleStyle = computed(() => createTextStyle(title.titleTextColor));

const apiErrorBannerStyle = computed(() =>
  combineStyles(
    createBackgroundStyle(apiErrorBanner.backgroundColor),
    createBorderStyle(apiErrorBanner.borderColor),
    createTextStyle(apiErrorBanner.textColor)
  )
);

const guestFormValidationCtx = computed(() => ({
  showDescriptionBox: showDescriptionBox.value,
  minDescription: MIN_DESCRIPTION_CHAR_NUM,
  maxDescription: MAX_GUEST_FIELDS_CHAR.description,
  nameOptional: requirements.nameOptional.value,
  // Email stays required in the open-request flow when OTP is on (OTP needs an email).
  emailOptional: requirements.emailOptional.value && !emailRequiredByOtp.value,
  descriptionOptional: requirements.descriptionOptional.value,
  phoneOptional: requirements.phoneOptional.value,
}));

/**
 * Description to submit. Only when the field is optional and left blank do we fall back to a
 * placeholder; when it is required, validation already forces the user to provide one.
 */
const resolvedDescription = computed(() =>
  requirements.descriptionOptional.value
    ? form.description.trim() || FALLBACK_OPEN_REQUEST_DESCRIPTION
    : form.description.trim()
);

const submitButtonText = computed(() => {
  const custom = primaryAction.buttonText.value?.trim();
  return custom || (t('page.guest.button.proceed') as string);
});

const isSubmitDisabled = computed(() => loading.value);

const submitButtonBackgroundColor = computed(() =>
  resolveButtonStateColor(
    isSubmitDisabled.value,
    primaryAction.disabledButtonBackgroundColor.value,
    primaryAction.buttonBackgroundColor.value
  )
);

const submitButtonBorderColor = computed(() =>
  resolveButtonStateColor(
    isSubmitDisabled.value,
    primaryAction.disabledButtonBorderColor.value,
    primaryAction.buttonBorderColor.value
  )
);

const submitButtonTextColor = computed(() =>
  resolveButtonStateColor(
    isSubmitDisabled.value,
    primaryAction.disabledButtonTextColor.value,
    primaryAction.buttonTextColor.value
  )
);

const showFormBackOrCancelButton = computed(() => {
  // Skip-to-live-agent opens guest form directly (no login step to return to).
  if (isSkipToLiveAgentFlow(connectConfig.value)) {
    return false;
  }

  return true;
});

const formNavigationButtonText = computed(() => {
  const custom = backButton.buttonText.value?.trim();
  if (custom) {
    return custom;
  }
  if (props.isOpenRequestFlow) {
    return t('global.cancel', 'Cancel') as string;
  }
  return t('global.back') as string;
});

const backButtonStyle = computed(() =>
  combineStyles(
    createBackgroundStyle(backButton.buttonBackgroundColor),
    createBorderStyle(backButton.buttonBorderColor),
    createTextStyle(backButton.buttonTextColor)
  )
);

const descriptionFieldErrorText = computed(() => {
  if (!error.description) {
    return '';
  }
  if (isDescriptionExceed.value) {
    return t('page.guest.description.exceed', { maxChar: MAX_GUEST_FIELDS_CHAR.description });
  }
  return error.description;
});

const displayFieldErrors = computed(
  (): GuestFieldErrors => ({
    name: error.name,
    phone: error.phone,
    email: error.email,
    description: descriptionFieldErrorText.value,
  })
);

const guestFormVModel = computed({
  get: (): GuestFormBoundModel => form,
  set(v: GuestFormBoundModel) {
    Object.assign(form, v);
  },
});

const handleClearGuestField = (field: keyof GuestFieldErrors) => {
  error[field] = '';
};

onMounted(() => {
  const g = botUserStore.guestUser;

  if (g) {
    if (g.name) {
      form.name = g.name;
    }
    if (g.email) {
      form.email = g.email;
    }
    if (g.phone) {
      form.phone = g.phone;
    }
    if (g.description) {
      form.description = g.description;
    }
  }

  if (!form.description.trim()) {
    const fromWizard = unref(widgetConversation.supportWizard.description);
    if (typeof fromWizard === 'string' && fromWizard.trim()) {
      form.description = fromWizard;
    }
  }
});

const processGuestData = (userData: UserInformation) => {
  const displayName = resolveGuestDisplayName(form, getFallbackUserName());

  const config = {
    ...userData,
    isGuest: true,
    name: form.name.trim() || userData.name || displayName,
    email: form.email.trim() || userData.email || '',
    phone: form.phone.trim() || userData.phone || '',
  };

  const visitorInfo = {
    displayName,
    email: form.email.trim(),
    name: displayName,
    phone: form.phone.trim(),
    isGuest: true,
  };

  store.commit('user/setGuestFormData', visitorInfo);
  store.commit('user/updateGuestUser', config);
  store.commit('user/updatePleasedUser', config);

  botUserStore.setGuestUser(config);

  const event: VisitorInfoFilled = {
    visitorInfo,
    description: form.description,
    userData,
  };

  emit('submit:detail', event);
};

const sendOtp = async (options?: { stayOnOtpStep?: boolean }) => {
  loading.value = true;
  requestErrorMessage.value = '';

  analytics.track({
    name: GENERAL_EVENTS.GUEST_OTP_SEND_ATTEMPT,
    metadata: {
      email: form.email,
      name: form.name,
    },
  });

  try {
    await PleasedApi.sendOtpToEmail(form.email, {
      name: form.name,
    });

    analytics.track({
      name: GENERAL_EVENTS.GUEST_OTP_SEND_SUCCESS,
      metadata: {
        email: form.email,
      },
    });

    originalEmail.value = form.email;
    currentStep.value = 'otp';
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : '';

    analytics.trackCriticalEvent({
      name: GENERAL_EVENTS.GUEST_OTP_SEND_FAILED,
      metadata: {
        email: form.email,
        error: message || 'Unknown error',
      },
    });
    captureEventException(err, ErrorTrackingEvent.GuestForm);

    requestErrorMessage.value = message || t('page.guest.otp_send_failed', 'Failed to send OTP. Please try again.');
    if (!options?.stayOnOtpStep) {
      goToForm();
    }
    throw err;
  } finally {
    loading.value = false;
  }
};

const resendOtp = () => sendOtp({ stayOnOtpStep: true });

const goToForm = () => {
  currentStep.value = 'form';
};

const handleSubmit = async () => {
  const { valid, errors } = await validateGuestTicketForm(form, guestFormValidationCtx.value);

  error.name = fieldErrorMessage(errors.name);
  error.phone = fieldErrorMessage(errors.phone);
  error.email = fieldErrorMessage(errors.email);
  error.description = fieldErrorMessage(errors.description);

  if (!valid) {
    return;
  }

  requestErrorMessage.value = '';

  if (props.isOpenRequestFlow) {
    if (!otpEnabled.value) {
      loading.value = true;
      try {
        const response = await createGuestOpenRequest(form, {
          subject: connectRequestSubject.value,
          description: resolvedDescription.value,
        });
        createdTicketId.value = response.id.toString();
        BridgeEvents.emitOpenRequestCreated(response, {
          description: resolvedDescription.value,
          chatbotSessionId: analytics.sessionId,
        });
        store.commit('user/clearGuestFormData');
        botUserStore.clearGuestFormDraft();
        currentStep.value = 'confirmation';
      } catch (err: unknown) {
        // Safety net: createGuestOpenRequest already reports GuestForm (WeakSet dedupes).
        captureEventException(err, ErrorTrackingEvent.GuestForm);
        const message = err instanceof Error ? err.message : '';
        requestErrorMessage.value = message || (t('global.ticket.ticket_creation_failed.start') as string);
        goToForm();
      } finally {
        loading.value = false;
      }
      return;
    }
    if (originalEmail.value && form.email === originalEmail.value) {
      currentStep.value = 'otp';
      return;
    }
    try {
      await sendOtp();
    } catch {
      // UI + GuestForm already handled in sendOtp.
    }
    return;
  }

  loading.value = true;
  try {
    const res = await PleasedApi.getAnonymousToken();
    processGuestData(res);
  } catch (err: unknown) {
    captureEventException(err, ErrorTrackingEvent.GuestForm);
    const message = err instanceof Error ? err.message : '';
    requestErrorMessage.value = message || (t('global.ticket.ticket_creation_failed.start') as string);
    loading.value = false;
  }
};

const handleOtpVerification = async (otpCode: string) => {
  analytics.track({
    name: GENERAL_EVENTS.GUEST_OTP_VERIFY_ATTEMPT,
    metadata: {
      email: form.email,
    },
  });

  try {
    reportingService.openRequestClicked();

    const ticketData = {
      email: form.email.trim(),
      code: otpCode,
      // Name is required by the API; use the resolved display name so an optional/blank name still submits.
      name: resolveGuestDisplayName(form, getFallbackUserName()),
      subject: connectRequestSubject.value,
      description: resolvedDescription.value,
      userLogs: reportingService.getLogs(),
      chatbotSessionId: analytics.sessionId,
    };

    const response = await PleasedApi.verifyOtpAndCreateTicket(ticketData);
    createdTicketId.value = response.id.toString();

    BridgeEvents.emitOpenRequestCreated(response, {
      description: resolvedDescription.value,
      chatbotSessionId: analytics.sessionId,
    });

    analytics.track({
      name: GENERAL_EVENTS.GUEST_OTP_VERIFY_SUCCESS,
      metadata: {
        email: form.email,
        ticketId: response.id.toString(),
      },
    });

    analytics.track({
      name: GENERAL_EVENTS.GUEST_TICKET_CREATED,
      metadata: {
        email: form.email,
        ticketId: response.id.toString(),
      },
    });

    requestErrorMessage.value = '';
    store.commit('user/clearGuestFormData');
    botUserStore.clearGuestFormDraft();
    currentStep.value = 'confirmation';
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : '';

    analytics.trackCriticalEvent({
      name: GENERAL_EVENTS.GUEST_OTP_VERIFY_FAILED,
      metadata: {
        email: form.email,
        error: message || 'Unknown error',
      },
    });
    captureEventException(err, ErrorTrackingEvent.GuestForm);

    throw err;
  }
};

const handleClose = () => {
  emit('close-chatbot');
};

const handleFormBackOrCancel = () => {
  if (isSkipToLiveAgentFlow(connectConfig.value)) {
    return;
  }

  if (props.isOpenRequestFlow || effectiveLoginMethod.value === 'none') {
    emit('support-request:cancel', {});
    return;
  }
  emit('back-to-login');
};

const handleFormBackOrCancelKeyDown = (event: KeyboardEvent) => {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }
  event.preventDefault();
  handleFormBackOrCancel();
};

onUnmounted(() => {
  loading.value = false;
});
</script>

<template>
  <div>
    <div
      v-if="currentStep === 'form'"
      class="shadow-hard theme-font-main widget-guest-form"
      role="region"
      :aria-label="titleDisplay"
    >
      <div class="widget-guest-form__form">
        <div class="widget-guest-form__title-row">
          <div class="theme-font-h4 widget-guest-form__title" :style="titleStyle">
            {{ titleDisplay }}
          </div>
          <div
            v-if="requestErrorMessage"
            class="theme-font-subtext widget-guest-form__api-error"
            :style="apiErrorBannerStyle"
          >
            <div class="widget-guest-form__api-error-icon" aria-hidden="true">
              <img :src="require('@/assets/img/error.png')" alt="" />
            </div>
            <div>{{ requestErrorMessage }}</div>
          </div>
        </div>

        <GuestFormFields
          v-model="guestFormVModel"
          :field-errors="displayFieldErrors"
          :show-description="showDescriptionBox"
          :description-hint="descriptionHint"
          :email-force-required="emailRequiredByOtp"
          class="widget-guest-form__open-request-fields"
          @clear-field-error="handleClearGuestField"
        />

        <div class="widget-guest-form__actions">
          <button
            v-if="showFormBackOrCancelButton"
            type="button"
            class="theme-font-main widget-guest-form__back"
            :style="backButtonStyle"
            :aria-label="formNavigationButtonText"
            tabindex="0"
            @click="handleFormBackOrCancel"
            @keydown="handleFormBackOrCancelKeyDown"
          >
            {{ formNavigationButtonText }}
          </button>
          <PlsButton1
            :aria-label="submitButtonText"
            :aria-busy="loading"
            :border-color="submitButtonBorderColor"
            :background-color="submitButtonBackgroundColor"
            :text-color="submitButtonTextColor"
            :disabled="isSubmitDisabled"
            class="theme-font-main widget-guest-form__submit"
            :class="{ 'widget-guest-form__submit--paired': showFormBackOrCancelButton }"
            @click="handleSubmit"
          >
            <span class="widget-guest-form__submit-content">
              <LoadingIcon v-if="loading" class="widget-guest-form__submit-spinner" aria-hidden="true" />
              <span class="widget-guest-form__submit-label">{{ submitButtonText }}</span>
            </span>
          </PlsButton1>
        </div>
      </div>
    </div>

    <GuestFormOtpVerification
      v-else-if="currentStep === 'otp'"
      :guest-data="guestData"
      :on-verify-success="handleOtpVerification"
      :on-resend-otp="resendOtp"
      @change-email="goToForm"
      @support-request:cancel="emit('support-request:cancel', $event)"
    />

    <GuestFormTicketConfirmation
      v-else-if="currentStep === 'confirmation'"
      :ticket-id="createdTicketId"
      :email="form.email"
      @close="handleClose"
    />
  </div>
</template>

<style lang="scss" scoped>
.widget-guest-form {
  width: 100%;
  box-sizing: border-box;
  padding: 12px 16px 16px;
}

.widget-guest-form__form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.widget-guest-form__title-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  margin-bottom: 4px;
}

.widget-guest-form__api-error {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  max-width: 22.3rem;
}

.widget-guest-form__api-error-icon {
  height: 1.5rem;
  flex-shrink: 0;

  img {
    width: auto;
    height: 100%;
    display: block;
  }
}

.widget-guest-form__field {
  width: 100%;
}

.widget-guest-form__open-request-fields {
  width: 100%;
}

.widget-guest-form__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  gap: 12px;
  margin-top: 4px;
}

.widget-guest-form__back {
  margin: 0;
  flex: 1 1 calc(50% - 6px);
  min-width: min(36vw, 160px);
  max-width: calc(50% - 6px);
  padding: 12px 16px;
  cursor: pointer;
  text-align: center;
  border-radius: 8px;
  box-sizing: border-box;
  border: 2px solid transparent;
}

.widget-guest-form__submit {
  min-width: min(40vw, 200px);
  padding: 12px 20px;
}

.widget-guest-form__submit--paired {
  min-width: min(36vw, 160px);
  flex: 1 1 calc(50% - 6px);
  max-width: calc(50% - 6px);
  box-sizing: border-box;
}

.widget-guest-form__submit-content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.widget-guest-form__submit-spinner {
  flex-shrink: 0;
}
</style>
